import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Group,
  MultiSelect,
  NumberInput,
  Popover,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconCheck,
  IconPencil,
  IconPlus,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import { useState } from 'react';
import PageLoader from 'src/UI/loader/PageLoader';

import {
  type LlmModel,
  type LlmProvider,
  type LlmTaskWithPool,
  type Modality,
  type ReasoningEffort,
  useAddModel,
  useAddModelToPool,
  useDeleteModel,
  useLlmModels,
  useLlmProviders,
  useLlmTasks,
  useRemoveModelFromPool,
  useRemoveModelModality,
  useSetModelEnabled,
  useSetModelModality,
  useSetPoolMemberEnabled,
  useSetPoolMemberPreferred,
  useSetProviderEnabled,
  useSetTaskModality,
  useSetTaskReasoningEffort,
  useUpdateModel,
} from './hooks/useLlmCatalogue';

const MODALITY_OPTIONS: Modality[] = ['TEXT', 'STRUCTURED', 'AUDIO'];

const REASONING_EFFORT_OPTIONS: ReasoningEffort[] = [
  'none',
  'low',
  'medium',
  'high',
  'xhigh',
  'max',
];

const TASK_EFFORT_OPTIONS = REASONING_EFFORT_OPTIONS.filter(effort => effort !== 'none');

const errorMessage = (e: unknown) => (e instanceof Error ? e.message : 'Unknown error');

type ReportError = (title: string, message: string) => void;

export default function LlmDispatcherTab() {
  const { tasks, isLoading: tasksLoading } = useLlmTasks();
  const { models, isLoading: modelsLoading } = useLlmModels();
  const { providers, isLoading: providersLoading } = useLlmProviders();
  const [error, setError] = useState<{ title: string; message: string } | null>(null);

  if (tasksLoading || modelsLoading || providersLoading) {
    return <PageLoader />;
  }

  const reportError: ReportError = (title, message) => setError({ title, message });

  return (
    <Stack gap="lg">
      <Box>
        <Text fw={600} size="lg">
          LLM dispatcher
        </Text>
        <Text size="sm" c="dimmed">
          Tasks and the model pools the dispatcher resolves them against. Add or remove models from
          each pool, or disable a model for a single task without removing it. Disabling is refused
          if it would leave a pool with no enabled models.
        </Text>
      </Box>

      {error && (
        <Alert
          color="red"
          icon={<IconAlertTriangle size={16} />}
          title={error.title}
          withCloseButton
          onClose={() => setError(null)}
        >
          {error.message}
        </Alert>
      )}

      <Stack gap="md">
        {(tasks ?? []).map(task => (
          <TaskPoolCard
            key={task.id}
            task={task}
            allModels={models ?? []}
            providers={providers ?? []}
            reportError={reportError}
          />
        ))}
        {(tasks ?? []).length === 0 && (
          <Text size="sm" c="dimmed">
            No tasks defined yet.
          </Text>
        )}
      </Stack>

      <Box>
        <Text fw={600} size="md" mb="xs">
          Model catalogue
        </Text>
        <Text size="sm" c="dimmed" mb="sm">
          All models known to the dispatcher, grouped by provider. Toggle a single model or every
          model under a provider. Disabling is refused if it would leave any task pool with no
          enabled models.
        </Text>
        <ProviderCatalogue
          providers={providers ?? []}
          models={models ?? []}
          reportError={reportError}
        />
      </Box>
    </Stack>
  );
}

function TaskPoolCard({
  task,
  allModels,
  providers,
  reportError,
}: {
  task: LlmTaskWithPool;
  allModels: LlmModel[];
  providers: LlmProvider[];
  reportError: ReportError;
}) {
  const { addModelToPool, isLoading: isAdding } = useAddModelToPool();
  const { removeModelFromPool, isLoading: isRemoving } = useRemoveModelFromPool();
  const { setPoolMemberEnabled, isLoading: isTogglingMembership } = useSetPoolMemberEnabled();
  const { setPoolMemberPreferred, isLoading: isTogglingPreferred } = useSetPoolMemberPreferred();
  const { setTaskModality, isLoading: isSavingModality } = useSetTaskModality();
  const { setTaskReasoningEffort, isLoading: isSavingEffort } = useSetTaskReasoningEffort();
  const [pendingModelId, setPendingModelId] = useState<string | null>(null);
  const [editingModality, setEditingModality] = useState<Modality | null>(null);
  const [isEditingEffort, setIsEditingEffort] = useState(false);
  const [editingEffort, setEditingEffort] = useState<ReasoningEffort | null>(task.reasoningEffort);

  const providerEnabledByKey = new Map(providers.map(p => [p.key, p.enabled]));
  const poolModelIds = new Set(task.pool.map(e => e.model.id));
  const addableModels = allModels.filter(
    m =>
      !poolModelIds.has(m.id) &&
      m.enabled &&
      providerEnabledByKey.get(m.providerKey) !== false &&
      m.modalities.some(mm => mm.modality === task.modality),
  );

  const modelLabel = (modelId: string) => {
    const model = allModels.find(m => m.id === modelId);
    return model ? `${model.providerKey}/${model.name}` : modelId;
  };

  const handleAdd = async () => {
    if (!pendingModelId) return;
    try {
      await addModelToPool({ taskId: task.id, modelId: pendingModelId });
      setPendingModelId(null);
      notifications.show({ message: 'Model added to pool', color: 'green' });
    } catch (e) {
      reportError(`Failed to add ${modelLabel(pendingModelId)} to ${task.key}`, errorMessage(e));
    }
  };

  const handleRemove = async (modelId: string) => {
    try {
      await removeModelFromPool({ taskId: task.id, modelId });
      notifications.show({ message: 'Model removed from pool', color: 'green' });
    } catch (e) {
      reportError(`Failed to remove ${modelLabel(modelId)} from ${task.key}`, errorMessage(e));
    }
  };

  const handleSaveModality = async () => {
    if (editingModality === null || editingModality === task.modality) return;
    try {
      await setTaskModality({ taskId: task.id, modality: editingModality });
      setEditingModality(null);
      notifications.show({ message: `Task modality set to ${editingModality}`, color: 'green' });
    } catch (e) {
      reportError(`Failed to change modality of ${task.key}`, errorMessage(e));
    }
  };

  const handleSaveEffort = async () => {
    if (editingEffort === task.reasoningEffort) return;
    try {
      await setTaskReasoningEffort({ taskId: task.id, reasoningEffort: editingEffort });
      setIsEditingEffort(false);
      notifications.show({
        message: `Task reasoning effort set to ${editingEffort ?? 'model default'}`,
        color: 'green',
      });
    } catch (e) {
      reportError(`Failed to change reasoning effort of ${task.key}`, errorMessage(e));
    }
  };

  const handleMembershipToggle = async (modelId: string, enabled: boolean) => {
    try {
      await setPoolMemberEnabled({ taskId: task.id, modelId, enabled });
      notifications.show({
        message: enabled ? 'Model enabled for this task' : 'Model disabled for this task',
        color: 'green',
      });
    } catch (e) {
      reportError(
        `Failed to ${enabled ? 'enable' : 'disable'} ${modelLabel(modelId)} for ${task.key}`,
        errorMessage(e),
      );
    }
  };

  const handlePreferredToggle = async (modelId: string, preferred: boolean) => {
    try {
      await setPoolMemberPreferred({ taskId: task.id, modelId, preferred });
      notifications.show({
        message: preferred
          ? 'Model preferred for this task; the rest of the pool is now fallback'
          : 'Model no longer preferred for this task',
        color: 'green',
      });
    } catch (e) {
      reportError(
        `Failed to ${preferred ? 'prefer' : 'unprefer'} ${modelLabel(modelId)} for ${task.key}`,
        errorMessage(e),
      );
    }
  };

  return (
    <Card withBorder p="md">
      <Group justify="space-between" mb="sm">
        <Box>
          <Text fw={600} size="sm">
            {task.displayName}
          </Text>
          <Text size="xs" c="dimmed" ff="monospace">
            {task.key}
          </Text>
        </Box>
        {editingModality === null ? (
          <Group gap="xs" wrap="nowrap">
            <Badge size="sm" variant="light">
              {task.modality}
            </Badge>
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={() => setEditingModality(task.modality)}
              aria-label="Edit task modality"
            >
              <IconPencil size={14} />
            </ActionIcon>
          </Group>
        ) : (
          <Group gap="xs" wrap="nowrap">
            <Select
              size="xs"
              data={MODALITY_OPTIONS}
              value={editingModality}
              onChange={value => value && setEditingModality(value as Modality)}
              w={140}
            />
            <ActionIcon
              variant="subtle"
              color="green"
              size="sm"
              loading={isSavingModality}
              disabled={editingModality === task.modality}
              onClick={handleSaveModality}
              aria-label="Save task modality"
            >
              <IconCheck size={14} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={() => setEditingModality(null)}
              disabled={isSavingModality}
              aria-label="Cancel modality edit"
            >
              <IconX size={14} />
            </ActionIcon>
          </Group>
        )}
      </Group>

      <Group gap="xs" mb="sm" wrap="nowrap">
        <Text size="xs" c="dimmed">
          Reasoning effort
        </Text>
        {isEditingEffort ? (
          <>
            <Select
              size="xs"
              clearable
              placeholder="model default"
              data={TASK_EFFORT_OPTIONS}
              value={editingEffort}
              onChange={value => setEditingEffort(value as ReasoningEffort | null)}
              w={160}
            />
            <ActionIcon
              variant="subtle"
              color="green"
              size="sm"
              loading={isSavingEffort}
              disabled={editingEffort === task.reasoningEffort}
              onClick={handleSaveEffort}
              aria-label="Save task reasoning effort"
            >
              <IconCheck size={14} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={() => setIsEditingEffort(false)}
              disabled={isSavingEffort}
              aria-label="Cancel reasoning effort edit"
            >
              <IconX size={14} />
            </ActionIcon>
          </>
        ) : (
          <>
            <Badge size="sm" variant="light" color={task.reasoningEffort ? 'blue' : 'gray'}>
              {task.reasoningEffort ?? 'model default'}
            </Badge>
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={() => {
                setEditingEffort(task.reasoningEffort);
                setIsEditingEffort(true);
              }}
              aria-label="Edit task reasoning effort"
            >
              <IconPencil size={14} />
            </ActionIcon>
          </>
        )}
      </Group>

      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Provider</Table.Th>
            <Table.Th>Model</Table.Th>
            <Table.Th>Model status</Table.Th>
            <Table.Th w={130}>Enabled for task</Table.Th>
            <Table.Th w={100}>Preferred</Table.Th>
            <Table.Th w={60} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {task.pool.map(({ model, enabled: poolEnabled, preferred }) => {
            const providerEnabled = providerEnabledByKey.get(model.providerKey) !== false;
            const effectivelyLive = poolEnabled && model.enabled && providerEnabled;
            return (
              <Table.Tr key={model.id} opacity={effectivelyLive ? 1 : 0.55}>
                <Table.Td>
                  <Group gap="xs" wrap="nowrap">
                    <Text size="sm">{model.providerKey}</Text>
                    {!providerEnabled && (
                      <Badge size="xs" color="orange" variant="light">
                        Provider off
                      </Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{model.name}</Text>
                </Table.Td>
                <Table.Td>
                  {model.enabled ? (
                    <Badge size="sm" color="green" variant="light">
                      Enabled
                    </Badge>
                  ) : (
                    <Badge size="sm" color="gray" variant="light">
                      Disabled
                    </Badge>
                  )}
                </Table.Td>
                <Table.Td>
                  <Switch
                    checked={poolEnabled}
                    onChange={event =>
                      handleMembershipToggle(model.id, event.currentTarget.checked)
                    }
                    disabled={isTogglingMembership}
                  />
                </Table.Td>
                <Table.Td>
                  <Switch
                    checked={preferred}
                    onChange={event => handlePreferredToggle(model.id, event.currentTarget.checked)}
                    disabled={isTogglingPreferred}
                    color="grape"
                  />
                </Table.Td>
                <Table.Td>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size="sm"
                    loading={isRemoving}
                    onClick={() => handleRemove(model.id)}
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            );
          })}
          {task.pool.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={6}>
                <Text size="sm" c="dimmed">
                  No models in this pool yet.
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>

      <Group mt="sm" gap="xs">
        <Select
          size="xs"
          placeholder="Pick a model"
          data={addableModels.map(m => ({
            value: m.id,
            label: `${m.providerKey} / ${m.name}`,
          }))}
          value={pendingModelId}
          onChange={setPendingModelId}
          searchable
          disabled={addableModels.length === 0}
          style={{ flex: 1 }}
        />
        <ActionIcon
          variant="filled"
          color="blue"
          size="md"
          disabled={!pendingModelId}
          loading={isAdding}
          onClick={handleAdd}
        >
          <IconPlus size={16} />
        </ActionIcon>
      </Group>
    </Card>
  );
}

function ProviderCatalogue({
  providers,
  models,
  reportError,
}: {
  providers: LlmProvider[];
  models: LlmModel[];
  reportError: ReportError;
}) {
  if (providers.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No providers in the catalogue yet.
      </Text>
    );
  }

  const modelsByProviderKey = new Map<string, LlmModel[]>();
  for (const model of models) {
    const existing = modelsByProviderKey.get(model.providerKey) ?? [];
    existing.push(model);
    modelsByProviderKey.set(model.providerKey, existing);
  }
  const orderedProviders = [...providers].sort((a, b) => a.key.localeCompare(b.key));

  return (
    <Stack gap="md">
      {orderedProviders.map(provider => (
        <ProviderCard
          key={provider.id}
          provider={provider}
          models={modelsByProviderKey.get(provider.key) ?? []}
          reportError={reportError}
        />
      ))}
    </Stack>
  );
}

function ProviderCard({
  provider,
  models,
  reportError,
}: {
  provider: LlmProvider;
  models: LlmModel[];
  reportError: ReportError;
}) {
  const providerKey = provider.key;
  const providerId = provider.id;
  const { setModelEnabled, isLoading: isSettingModel } = useSetModelEnabled();
  const { setProviderEnabled, isLoading: isSettingProvider } = useSetProviderEnabled();
  const { updateModel, isLoading: isSavingModel } = useUpdateModel();
  const { deleteModel, isLoading: isDeletingModel } = useDeleteModel();
  const { addModel, isLoading: isAddingModel } = useAddModel();
  const [editing, setEditing] = useState<{
    modelId: string;
    name: string;
    tpmLimit: number | null;
    rpmLimit: number | null;
    maxInputTokens: number;
    reasoningEffort: ReasoningEffort | null;
    temperature: number | null;
  } | null>(null);
  const [adding, setAdding] = useState<{
    name: string;
    modalities: Modality[];
    tpmLimit: number | null;
    rpmLimit: number | null;
    maxInputTokens: number;
    reasoningEffort: ReasoningEffort | null;
    temperature: number | null;
  } | null>(null);

  const providerHasLimits = provider.tpmLimit !== null || provider.rpmLimit !== null;

  // Mirrors the backend scope rule: rate limits live on exactly one of provider or
  // model, so provider-limited providers need both model limits blank, and the rest
  // need at least one set.
  const modelLimitsValid = (tpmLimit: number | null, rpmLimit: number | null) => {
    if ((tpmLimit !== null && tpmLimit <= 0) || (rpmLimit !== null && rpmLimit <= 0)) return false;
    return providerHasLimits
      ? tpmLimit === null && rpmLimit === null
      : tpmLimit !== null || rpmLimit !== null;
  };

  const modelLimitsError = (tpmLimit: number | null, rpmLimit: number | null) => {
    if (providerHasLimits && (tpmLimit !== null || rpmLimit !== null)) {
      return 'Limits are set on the provider — leave blank';
    }
    if (!providerHasLimits && tpmLimit === null && rpmLimit === null) {
      return 'Set at least one limit';
    }
    return undefined;
  };

  const trimmedNewName = adding?.name.trim() ?? '';
  const newNameConflicts = trimmedNewName.length > 0 && models.some(m => m.name === trimmedNewName);
  const addingValid =
    adding !== null &&
    trimmedNewName.length > 0 &&
    !newNameConflicts &&
    adding.modalities.length > 0 &&
    modelLimitsValid(adding.tpmLimit, adding.rpmLimit) &&
    adding.maxInputTokens > 0;

  const startAdding = () =>
    setAdding({
      name: '',
      modalities: ['TEXT'],
      tpmLimit: null,
      rpmLimit: null,
      maxInputTokens: 0,
      reasoningEffort: null,
      temperature: null,
    });

  const handleSaveNewModel = async () => {
    if (!adding || !addingValid) return;
    try {
      await addModel({
        providerKey,
        name: trimmedNewName,
        maxInputTokens: adding.maxInputTokens,
        tpmLimit: adding.tpmLimit,
        rpmLimit: adding.rpmLimit,
        reasoningEffort: adding.reasoningEffort,
        temperature: adding.temperature,
        modalities: adding.modalities.map(m => ({ modality: m, scaleFactor: 1 })),
      });
      setAdding(null);
      notifications.show({ message: `Added ${trimmedNewName}`, color: 'green' });
    } catch (e) {
      reportError(`Failed to add ${providerKey}/${trimmedNewName}`, errorMessage(e));
    }
  };

  const startEditing = (model: LlmModel) =>
    setEditing({
      modelId: model.id,
      name: model.name,
      tpmLimit: model.tpmLimit,
      rpmLimit: model.rpmLimit,
      maxInputTokens: model.maxInputTokens,
      reasoningEffort: model.reasoningEffort,
      temperature: model.temperature,
    });

  const handleSaveModel = async (model: LlmModel) => {
    if (!editing || editing.modelId !== model.id) return;
    const trimmedName = editing.name.trim();
    const { tpmLimit, rpmLimit, maxInputTokens, reasoningEffort, temperature } = editing;
    if (trimmedName.length === 0 || !modelLimitsValid(tpmLimit, rpmLimit) || maxInputTokens <= 0) {
      return;
    }
    try {
      await updateModel({
        modelId: model.id,
        name: trimmedName,
        tpmLimit,
        rpmLimit,
        maxInputTokens,
        reasoningEffort,
        temperature,
      });
      setEditing(null);
      notifications.show({ message: `Updated ${trimmedName}`, color: 'green' });
    } catch (e) {
      reportError(`Failed to update ${providerKey}/${model.name}`, errorMessage(e));
    }
  };

  const handleDeleteModel = async (model: LlmModel) => {
    try {
      await deleteModel({ modelId: model.id });
      notifications.show({ message: `Deleted ${model.name}`, color: 'green' });
    } catch (e) {
      reportError(`Failed to delete ${providerKey}/${model.name}`, errorMessage(e));
    }
  };

  const handleProviderToggle = async (enabled: boolean) => {
    try {
      await setProviderEnabled({ providerId, enabled });
      notifications.show({
        message: enabled ? `Enabled provider ${providerKey}` : `Disabled provider ${providerKey}`,
        color: 'green',
      });
    } catch (e) {
      reportError(
        `Failed to ${enabled ? 'enable' : 'disable'} provider ${providerKey}`,
        errorMessage(e),
      );
    }
  };

  const handleModelToggle = async (model: LlmModel, enabled: boolean) => {
    try {
      await setModelEnabled({ modelId: model.id, enabled });
      notifications.show({
        message: `${enabled ? 'Enabled' : 'Disabled'} ${model.name}`,
        color: 'green',
      });
    } catch (e) {
      reportError(
        `Failed to ${enabled ? 'enable' : 'disable'} ${providerKey}/${model.name}`,
        errorMessage(e),
      );
    }
  };

  return (
    <Card withBorder p="md">
      <Group justify="space-between" mb="sm">
        <Group gap="sm">
          <Text fw={600} size="sm" ff="monospace">
            {providerKey}
          </Text>
          {providerHasLimits && (
            <Badge size="sm" variant="light" color="grape">
              Provider-level limits: TPM {provider.tpmLimit?.toLocaleString() ?? '-'} · RPM{' '}
              {provider.rpmLimit?.toLocaleString() ?? '-'}
            </Badge>
          )}
        </Group>
        <Switch
          checked={provider.enabled}
          onChange={event => handleProviderToggle(event.currentTarget.checked)}
          label={provider.enabled ? 'Enabled' : 'Disabled'}
          labelPosition="left"
          disabled={isSettingProvider}
        />
      </Group>

      {!provider.enabled && (
        <Text size="xs" c="orange" mb="xs">
          Provider is disabled — none of these models are dispatchable, regardless of their
          individual enabled state.
        </Text>
      )}

      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Model</Table.Th>
            <Table.Th>Modalities</Table.Th>
            <Table.Th>TPM</Table.Th>
            <Table.Th>RPM</Table.Th>
            <Table.Th>Max input</Table.Th>
            <Table.Th>Reasoning</Table.Th>
            <Table.Th>Temperature</Table.Th>
            <Table.Th w={96} />
            <Table.Th w={120}>Enabled</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {models.map(model => {
            const isEditing = editing?.modelId === model.id;
            const trimmedEditName = isEditing ? editing.name.trim() : '';
            const editNameConflicts =
              isEditing &&
              trimmedEditName.length > 0 &&
              models.some(m => m.id !== model.id && m.name === trimmedEditName);
            const editValid =
              isEditing &&
              trimmedEditName.length > 0 &&
              !editNameConflicts &&
              modelLimitsValid(editing.tpmLimit, editing.rpmLimit) &&
              editing.maxInputTokens > 0;
            const editDirty =
              isEditing &&
              (trimmedEditName !== model.name ||
                editing.tpmLimit !== model.tpmLimit ||
                editing.rpmLimit !== model.rpmLimit ||
                editing.maxInputTokens !== model.maxInputTokens ||
                editing.reasoningEffort !== model.reasoningEffort ||
                editing.temperature !== model.temperature);

            const effectivelyLive = provider.enabled && model.enabled;
            return (
              <Table.Tr key={model.id} opacity={effectivelyLive ? 1 : 0.55}>
                <Table.Td>
                  {isEditing ? (
                    <TextInput
                      size="xs"
                      value={editing.name}
                      error={editNameConflicts ? 'Already exists' : undefined}
                      onChange={event =>
                        setEditing({ ...editing, name: event.currentTarget.value })
                      }
                    />
                  ) : (
                    <Text size="sm">{model.name}</Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <ModalitiesCell model={model} reportError={reportError} />
                </Table.Td>
                <Table.Td>
                  {isEditing ? (
                    <NumberInput
                      size="xs"
                      min={1}
                      thousandSeparator=","
                      value={editing.tpmLimit ?? ''}
                      error={modelLimitsError(editing.tpmLimit, editing.rpmLimit)}
                      onChange={value =>
                        setEditing({
                          ...editing,
                          tpmLimit: typeof value === 'number' ? value : null,
                        })
                      }
                    />
                  ) : (
                    <Text size="sm">{model.tpmLimit?.toLocaleString() ?? '-'}</Text>
                  )}
                </Table.Td>
                <Table.Td>
                  {isEditing ? (
                    <NumberInput
                      size="xs"
                      min={1}
                      thousandSeparator=","
                      value={editing.rpmLimit ?? ''}
                      error={modelLimitsError(editing.tpmLimit, editing.rpmLimit) !== undefined}
                      onChange={value =>
                        setEditing({
                          ...editing,
                          rpmLimit: typeof value === 'number' ? value : null,
                        })
                      }
                    />
                  ) : (
                    <Text size="sm">{model.rpmLimit?.toLocaleString() ?? '-'}</Text>
                  )}
                </Table.Td>
                <Table.Td>
                  {isEditing ? (
                    <NumberInput
                      size="xs"
                      min={1}
                      thousandSeparator=","
                      value={editing.maxInputTokens}
                      onChange={value =>
                        setEditing({
                          ...editing,
                          maxInputTokens: typeof value === 'number' ? value : 0,
                        })
                      }
                    />
                  ) : (
                    <Text size="sm">{model.maxInputTokens.toLocaleString()}</Text>
                  )}
                </Table.Td>
                <Table.Td>
                  {isEditing ? (
                    <Select
                      size="xs"
                      clearable
                      placeholder="provider default"
                      data={REASONING_EFFORT_OPTIONS}
                      value={editing.reasoningEffort}
                      onChange={value =>
                        setEditing({
                          ...editing,
                          reasoningEffort: value as ReasoningEffort | null,
                        })
                      }
                    />
                  ) : (
                    <Text size="sm">{model.reasoningEffort ?? '-'}</Text>
                  )}
                </Table.Td>
                <Table.Td>
                  {isEditing ? (
                    <NumberInput
                      size="xs"
                      min={0}
                      max={2}
                      step={0.1}
                      placeholder="provider default"
                      value={editing.temperature ?? ''}
                      onChange={value =>
                        setEditing({
                          ...editing,
                          temperature: typeof value === 'number' ? value : null,
                        })
                      }
                    />
                  ) : (
                    <Text size="sm">{model.temperature ?? '-'}</Text>
                  )}
                </Table.Td>
                <Table.Td>
                  {isEditing ? (
                    <Group gap="xs" wrap="nowrap">
                      <ActionIcon
                        variant="subtle"
                        color="green"
                        size="sm"
                        loading={isSavingModel}
                        disabled={!editValid || !editDirty}
                        onClick={() => handleSaveModel(model)}
                      >
                        <IconCheck size={14} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        onClick={() => setEditing(null)}
                        disabled={isSavingModel}
                      >
                        <IconX size={14} />
                      </ActionIcon>
                    </Group>
                  ) : (
                    <Group gap="xs" wrap="nowrap">
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        onClick={() => startEditing(model)}
                        disabled={editing !== null || adding !== null}
                      >
                        <IconPencil size={14} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="sm"
                        loading={isDeletingModel}
                        onClick={() => handleDeleteModel(model)}
                        disabled={editing !== null || adding !== null}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                  )}
                </Table.Td>
                <Table.Td>
                  <Switch
                    checked={model.enabled}
                    onChange={event => handleModelToggle(model, event.currentTarget.checked)}
                    disabled={isSettingModel || isSettingProvider}
                  />
                </Table.Td>
              </Table.Tr>
            );
          })}
          {models.length === 0 && !adding && (
            <Table.Tr>
              <Table.Td colSpan={9}>
                <Text size="sm" c="dimmed">
                  No models for this provider yet. Add one below.
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
          {adding && (
            <Table.Tr>
              <Table.Td>
                <TextInput
                  size="xs"
                  placeholder="model-name"
                  value={adding.name}
                  error={newNameConflicts ? 'Already exists' : undefined}
                  onChange={event => setAdding({ ...adding, name: event.currentTarget.value })}
                />
              </Table.Td>
              <Table.Td>
                <MultiSelect
                  size="xs"
                  data={MODALITY_OPTIONS}
                  value={adding.modalities}
                  onChange={values => setAdding({ ...adding, modalities: values as Modality[] })}
                  error={adding.modalities.length === 0 ? 'Pick at least one' : undefined}
                />
              </Table.Td>
              <Table.Td>
                <NumberInput
                  size="xs"
                  min={1}
                  thousandSeparator=","
                  placeholder={providerHasLimits ? 'On provider' : undefined}
                  value={adding.tpmLimit ?? ''}
                  error={modelLimitsError(adding.tpmLimit, adding.rpmLimit)}
                  onChange={value =>
                    setAdding({ ...adding, tpmLimit: typeof value === 'number' ? value : null })
                  }
                />
              </Table.Td>
              <Table.Td>
                <NumberInput
                  size="xs"
                  min={1}
                  thousandSeparator=","
                  placeholder={providerHasLimits ? 'On provider' : undefined}
                  value={adding.rpmLimit ?? ''}
                  error={modelLimitsError(adding.tpmLimit, adding.rpmLimit) !== undefined}
                  onChange={value =>
                    setAdding({ ...adding, rpmLimit: typeof value === 'number' ? value : null })
                  }
                />
              </Table.Td>
              <Table.Td>
                <NumberInput
                  size="xs"
                  min={1}
                  thousandSeparator=","
                  value={adding.maxInputTokens || ''}
                  onChange={value =>
                    setAdding({
                      ...adding,
                      maxInputTokens: typeof value === 'number' ? value : 0,
                    })
                  }
                />
              </Table.Td>
              <Table.Td>
                <Select
                  size="xs"
                  clearable
                  placeholder="provider default"
                  data={REASONING_EFFORT_OPTIONS}
                  value={adding.reasoningEffort}
                  onChange={value =>
                    setAdding({ ...adding, reasoningEffort: value as ReasoningEffort | null })
                  }
                />
              </Table.Td>
              <Table.Td>
                <NumberInput
                  size="xs"
                  min={0}
                  max={2}
                  step={0.1}
                  placeholder="provider default"
                  value={adding.temperature ?? ''}
                  onChange={value =>
                    setAdding({ ...adding, temperature: typeof value === 'number' ? value : null })
                  }
                />
              </Table.Td>
              <Table.Td>
                <Group gap="xs" wrap="nowrap">
                  <ActionIcon
                    variant="subtle"
                    color="green"
                    size="sm"
                    loading={isAddingModel}
                    disabled={!addingValid}
                    onClick={handleSaveNewModel}
                  >
                    <IconCheck size={14} />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    onClick={() => setAdding(null)}
                    disabled={isAddingModel}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                </Group>
              </Table.Td>
              <Table.Td>
                <Text size="xs" c="dimmed">
                  Enabled on save
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>

      {!adding && (
        <Group mt="sm">
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlus size={14} />}
            onClick={startAdding}
            disabled={editing !== null}
          >
            Add model
          </Button>
        </Group>
      )}
    </Card>
  );
}

function ModalitiesCell({ model, reportError }: { model: LlmModel; reportError: ReportError }) {
  const { setModelModality, isLoading: isSetting } = useSetModelModality();
  const { removeModelModality, isLoading: isRemoving } = useRemoveModelModality();
  const [opened, setOpened] = useState(false);
  const [draftScales, setDraftScales] = useState<Record<string, number>>({});
  const [pendingNew, setPendingNew] = useState<{ modality: Modality | null; scaleFactor: number }>({
    modality: null,
    scaleFactor: 1,
  });

  const supportedModalities = new Set(model.modalities.map(m => m.modality));
  const addableOptions = MODALITY_OPTIONS.filter(m => !supportedModalities.has(m));

  const resetAdd = () => setPendingNew({ modality: null, scaleFactor: 1 });

  const closeEditor = () => {
    setOpened(false);
    setDraftScales({});
    resetAdd();
  };

  const handleAdd = async () => {
    if (!pendingNew.modality || pendingNew.scaleFactor <= 0) return;
    try {
      await setModelModality({
        modelId: model.id,
        modality: pendingNew.modality,
        scaleFactor: pendingNew.scaleFactor,
      });
      resetAdd();
      notifications.show({ message: `Added modality ${pendingNew.modality}`, color: 'green' });
    } catch (e) {
      reportError(`Failed to add ${pendingNew.modality} to ${model.name}`, errorMessage(e));
    }
  };

  const handleUpdateScale = async (modality: Modality, scaleFactor: number) => {
    if (scaleFactor <= 0) return;
    try {
      await setModelModality({ modelId: model.id, modality, scaleFactor });
      setDraftScales(prev => {
        const next = { ...prev };
        delete next[modality];
        return next;
      });
      notifications.show({ message: `Updated scale factor for ${modality}`, color: 'green' });
    } catch (e) {
      reportError(`Failed to update ${modality} on ${model.name}`, errorMessage(e));
    }
  };

  const handleRemove = async (modality: Modality) => {
    try {
      await removeModelModality({ modelId: model.id, modality });
      notifications.show({ message: `Removed modality ${modality}`, color: 'green' });
    } catch (e) {
      reportError(`Failed to remove ${modality} from ${model.name}`, errorMessage(e));
    }
  };

  return (
    <Group gap="xs" wrap="wrap">
      {model.modalities.map(m => (
        <Badge key={m.modality} size="xs" variant="light">
          {m.modality}
          {m.scaleFactor !== 1 ? ` ×${m.scaleFactor}` : ''}
        </Badge>
      ))}
      <Popover
        opened={opened}
        onChange={value => (value ? setOpened(true) : closeEditor())}
        position="bottom-start"
        withArrow
        shadow="md"
      >
        <Popover.Target>
          <ActionIcon
            variant="subtle"
            size="xs"
            onClick={() => setOpened(o => !o)}
            aria-label="Edit modalities"
          >
            <IconPencil size={12} />
          </ActionIcon>
        </Popover.Target>
        <Popover.Dropdown>
          <Stack gap="xs" w={300}>
            <Text size="xs" fw={600}>
              Modalities for {model.name}
            </Text>
            {model.modalities.map(m => {
              const isText = m.modality === 'TEXT';
              const isOnlyModality = model.modalities.length <= 1;
              const draft = draftScales[m.modality];
              const currentValue = draft ?? m.scaleFactor;
              const dirty = draft !== undefined && draft !== m.scaleFactor;
              return (
                <Group key={m.modality} gap="xs" wrap="nowrap">
                  <Badge size="sm" variant="light" w={90}>
                    {m.modality}
                  </Badge>
                  {isText ? (
                    <Text size="xs" c="dimmed" style={{ flex: 1 }}>
                      scale fixed at 1.0
                    </Text>
                  ) : (
                    <NumberInput
                      size="xs"
                      min={0.01}
                      step={0.5}
                      decimalScale={2}
                      value={currentValue}
                      onChange={value =>
                        setDraftScales(prev => ({
                          ...prev,
                          [m.modality]: typeof value === 'number' ? value : 0,
                        }))
                      }
                      style={{ flex: 1 }}
                    />
                  )}
                  {!isText && (
                    <ActionIcon
                      variant="subtle"
                      color="green"
                      size="sm"
                      loading={isSetting}
                      disabled={!dirty || currentValue <= 0}
                      onClick={() => handleUpdateScale(m.modality, currentValue)}
                      aria-label={`Save ${m.modality} scale`}
                    >
                      <IconCheck size={14} />
                    </ActionIcon>
                  )}
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size="sm"
                    loading={isRemoving}
                    disabled={isOnlyModality}
                    title={
                      isOnlyModality ? 'A model must support at least one modality' : undefined
                    }
                    onClick={() => handleRemove(m.modality)}
                    aria-label={`Remove ${m.modality}`}
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Group>
              );
            })}
            {addableOptions.length > 0 && (
              <Group gap="xs" wrap="nowrap">
                <Select
                  size="xs"
                  placeholder="Add modality"
                  data={addableOptions}
                  value={pendingNew.modality}
                  onChange={value =>
                    setPendingNew({ modality: value as Modality | null, scaleFactor: 1 })
                  }
                  w={120}
                />
                {pendingNew.modality === 'TEXT' ? (
                  <Text size="xs" c="dimmed" style={{ flex: 1 }}>
                    scale fixed at 1.0
                  </Text>
                ) : (
                  <NumberInput
                    size="xs"
                    min={0.01}
                    step={0.5}
                    decimalScale={2}
                    value={pendingNew.scaleFactor}
                    onChange={value =>
                      setPendingNew(prev => ({
                        ...prev,
                        scaleFactor: typeof value === 'number' ? value : 0,
                      }))
                    }
                    style={{ flex: 1 }}
                  />
                )}
                <ActionIcon
                  variant="filled"
                  color="blue"
                  size="sm"
                  loading={isSetting}
                  disabled={!pendingNew.modality || pendingNew.scaleFactor <= 0}
                  onClick={handleAdd}
                  aria-label="Add modality"
                >
                  <IconPlus size={14} />
                </ActionIcon>
              </Group>
            )}
          </Stack>
        </Popover.Dropdown>
      </Popover>
    </Group>
  );
}
